import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { card as cardClass, caret as caretClass } from './notificationBell.css';

/** Minimal rect we need from the bell to position a card beneath it. */
export interface AnchorRect {
  bottom: number;
  right: number;
  width: number;
}

interface AnchoredCardProps {
  anchor: AnchorRect;
  width: number;
  zIndex?: number;
  /**
   * When set, a transparent backdrop + Escape close the card. Omit for passive,
   * timer-dismissed surfaces (the queued notice) so nothing — not even a modal
   * dialog closing underneath — can steal it away.
   */
  onDismiss?: () => void;
  role?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  maxHeight?: number | string;
  children: ReactNode;
}

/**
 * A card portalled to <body>, fixed beneath the bell with an upward caret
 * pointing at it — so the notice and the panel both read as belonging to the
 * bell rather than floating loose. No Radix dismissal layer (which fought the
 * export dialog's modal focus); dismissal, when wanted, is a plain backdrop.
 */
export function AnchoredCard({
  anchor,
  width,
  zIndex = 2000,
  onDismiss,
  role,
  maxHeight,
  children,
  ...rest
}: AnchoredCardProps) {
  useEffect(() => {
    if (!onDismiss) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const top = anchor.bottom + 10;
  const right = Math.max(8, window.innerWidth - anchor.right);
  // The card's right edge sits at the bell's right edge, so half the bell's
  // width places the caret under the bell's centre.
  const caretRight = Math.max(12, anchor.width / 2 - 6);

  return createPortal(
    <>
      {onDismiss && (
        <div
          onClick={onDismiss}
          aria-hidden
          style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: zIndex - 1, background: 'transparent' }}
        />
      )}
      <div
        role={role}
        aria-label={rest['aria-label']}
        data-testid={rest['data-testid']}
        className={cardClass}
        style={{
          position: 'fixed',
          top,
          right,
          width,
          maxWidth: 'calc(100vw - 16px)',
          maxHeight,
          zIndex,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <span className={caretClass} style={{ right: caretRight }} aria-hidden />
        {children}
      </div>
    </>,
    document.body,
  );
}
